import {
  MAX_LAYOUT_TEXT_LENGTH,
  NO_LAYOUT_OWNER,
  validateLayoutRequest,
  validateLayoutResponse,
  type LayoutDotBuffers,
  type LayoutLetterMetadata,
  type LayoutResult,
  type LayoutTargetBuffers,
  type LayoutWordMetadata,
  type PackedLayoutSnapshot,
} from './LayoutProtocol.js'
import { MAX_LAYOUT_OFFSET_COUNT } from '../project/ProjectDocument.js'
import { MAX_PARTICLE_COUNT } from '../engine/particles/particleBudget.js'
import { PERSIAN_SHORT_CONNECTORS } from './persianConnectors.js'

export const MAX_LAYOUT_STRIPE_ROWS = 64
export const MAX_LAYOUT_DOT_COUNT = MAX_PARTICLE_COUNT
export const MAX_LAYOUT_COMPONENT_SCRATCH_PIXELS = 1_000_000
export const MAX_LAYOUT_INTERVAL_REFERENCES = 1_000_000

export type RasterWord = LayoutWordMetadata
export type RasterLetter = LayoutLetterMetadata

export interface LayoutRaster {
  readonly width: number
  readonly height: number
  readonly words: readonly RasterWord[]
  readonly letters: readonly RasterLetter[]
  readAlphaStripe(y: number, rows: number): Uint8Array
  /**
   * Ink-truth word ownership: per pixel the 1-based POSITION in `words` of
   * the word whose paint actually covers it (0 = no trustworthy ink owner).
   * Font metrics underreport Nastaliq ink extent, so ownership must come
   * from painted ink; 0-valued pixels fall back to the geometric
   * rectangle/nearest-word attribution.
   */
  readOwnerStripe(y: number, rows: number): Uint32Array
}

export interface LayoutRasterizer {
  rasterize(snapshot: PackedLayoutSnapshot): LayoutRaster | Promise<LayoutRaster>
}

export interface LayoutCanvasContext2D {
  fillStyle: string
  font: string
  textBaseline: string
  textAlign: string
  direction: string
  clearRect(x: number, y: number, width: number, height: number): void
  measureText(text: string): {
    readonly width: number
    readonly actualBoundingBoxAscent?: number
    readonly actualBoundingBoxDescent?: number
    readonly actualBoundingBoxLeft?: number
    readonly actualBoundingBoxRight?: number
  }
  fillText(text: string, x: number, y: number): void
  getImageData(
    x: number,
    y: number,
    width: number,
    height: number,
  ): { readonly data: ArrayLike<number> }
}

export interface LayoutCanvasSurface {
  readonly width: number
  readonly height: number
  getContext(
    type: '2d',
    options: { readonly willReadFrequently: true },
  ): LayoutCanvasContext2D | null
}

export type LayoutCanvasFactory = (width: number, height: number) => LayoutCanvasSurface

export class LayoutCanvasUnsupportedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LayoutCanvasUnsupportedError'
  }
}

interface MeasuredText {
  readonly width: number
  readonly actualBoundingBoxAscent?: number
  readonly actualBoundingBoxDescent?: number
  readonly actualBoundingBoxLeft?: number
  readonly actualBoundingBoxRight?: number
}

function measuredText(context: LayoutCanvasContext2D, text: string): MeasuredText {
  const metrics = context.measureText(text)
  const width = metrics.width
  if (!Number.isFinite(width) || width < 0) {
    throw new RangeError('Canvas text measurement must return a finite non-negative width')
  }
  const measurement: {
    width: number
    actualBoundingBoxAscent?: number
    actualBoundingBoxDescent?: number
    actualBoundingBoxLeft?: number
    actualBoundingBoxRight?: number
  } = { width }
  const actualBoundingBoxAscent = metrics.actualBoundingBoxAscent
  const actualBoundingBoxDescent = metrics.actualBoundingBoxDescent
  if (
    typeof actualBoundingBoxAscent === 'number'
    && Number.isFinite(actualBoundingBoxAscent)
    && typeof actualBoundingBoxDescent === 'number'
    && Number.isFinite(actualBoundingBoxDescent)
    && Number.isFinite(actualBoundingBoxAscent + actualBoundingBoxDescent)
    && actualBoundingBoxAscent + actualBoundingBoxDescent >= 0
  ) {
    measurement.actualBoundingBoxAscent = actualBoundingBoxAscent
    measurement.actualBoundingBoxDescent = actualBoundingBoxDescent
  }
  const actualBoundingBoxLeft = metrics.actualBoundingBoxLeft
  const actualBoundingBoxRight = metrics.actualBoundingBoxRight
  if (
    typeof actualBoundingBoxLeft === 'number'
    && Number.isFinite(actualBoundingBoxLeft)
    && typeof actualBoundingBoxRight === 'number'
    && Number.isFinite(actualBoundingBoxRight)
    && Number.isFinite(actualBoundingBoxLeft + actualBoundingBoxRight)
  ) {
    measurement.actualBoundingBoxLeft = actualBoundingBoxLeft
    measurement.actualBoundingBoxRight = actualBoundingBoxRight
  }
  return measurement
}

function measuredVerticalBounds(
  measurement: MeasuredText,
  baselineY: number,
  fallbackY: number,
  fallbackHeight: number,
): { readonly y: number; readonly height: number } {
  const ascent = measurement.actualBoundingBoxAscent
  const descent = measurement.actualBoundingBoxDescent
  return ascent === undefined || descent === undefined
    ? { y: fallbackY, height: fallbackHeight }
    : { y: baselineY - ascent, height: ascent + descent }
}

/**
 * Horizontal ownership bounds for a word anchored at `anchorX` (RTL,
 * textAlign 'right'): the union of the advance box `[anchorX - width, anchorX]`
 * and the measured ink box, so calligraphic overhang (swashes, sarkesh) that
 * paints outside the advance width is still owned instead of dropped.
 */
function measuredHorizontalBounds(
  measurement: MeasuredText,
  anchorX: number,
): { readonly x: number; readonly width: number } {
  const left = measurement.actualBoundingBoxLeft
  const right = measurement.actualBoundingBoxRight
  const leftExtent = left === undefined ? measurement.width : Math.max(measurement.width, left)
  const rightExtent = right === undefined ? 0 : Math.max(0, right)
  return { x: anchorX - leftExtent, width: leftExtent + rightExtent }
}

/**
 * Connector-aware word gap (ported from V1's persianTypography rules): short
 * connector words (که، از، به …) almost touch their neighbours, 1-2 char
 * words get a tighter gap, and every boundary keeps a hairline minimum so
 * words never collide. Unlike V1 the user's base gap is left unclamped for
 * normal boundaries so the word-spacing control stays effective across its
 * full range.
 */
function adjustedWordGap(baseGap: number, fontSize: number, currentWord: string, nextWord: string): number {
  const currentConnector = PERSIAN_SHORT_CONNECTORS.includes(currentWord)
  const nextConnector = PERSIAN_SHORT_CONNECTORS.includes(nextWord)
  let gap = baseGap
  if (currentConnector && nextConnector) gap = fontSize * 0.01
  else if (currentConnector || nextConnector) gap = fontSize * 0.02
  else if (currentWord.length <= 2 || nextWord.length <= 2) gap = baseGap * 0.6
  return Math.max(gap, fontSize * 0.005)
}

/**
 * Creates the one canonical Canvas2D paint/ownership model used by both the
 * worker OffscreenCanvas path and the lazy main-thread canvas fallback.
 */
export function createCanvasTextRasterizer(createCanvas: LayoutCanvasFactory): LayoutRasterizer {
  return {
    rasterize(snapshot) {
      const canvas = createCanvas(snapshot.width, snapshot.height)
      if (canvas.width !== snapshot.width || canvas.height !== snapshot.height) {
        throw new RangeError('Canvas factory returned mismatched dimensions')
      }
      const context = canvas.getContext('2d', { willReadFrequently: true })
      if (context === null) {
        throw new LayoutCanvasUnsupportedError('Canvas2D is unavailable for layout scanning')
      }
      context.clearRect(0, 0, snapshot.width, snapshot.height)
      context.fillStyle = '#fff'
      context.font = `${snapshot.fontSize}px ${JSON.stringify(snapshot.fontName)}`
      context.textBaseline = snapshot.baseline
      context.textAlign = 'right'
      context.direction = 'rtl'

      // Parallel ownership canvas: every word is painted a second time, one
      // word at a time, and all of its painted pixels are read back into a
      // persistent owner map (1-based word position, 0 = no ink owner).
      // Ink-truth ownership beats guessing from font-metric boxes,
      // which underreport Nastaliq overhang (swashes, sarkesh, dots).
      const ownerCanvas = createCanvas(snapshot.width, snapshot.height)
      if (ownerCanvas.width !== snapshot.width || ownerCanvas.height !== snapshot.height) {
        throw new RangeError('Canvas factory returned mismatched dimensions')
      }
      const ownerContext = ownerCanvas.getContext('2d', { willReadFrequently: true })
      if (ownerContext === null) {
        throw new LayoutCanvasUnsupportedError('Canvas2D is unavailable for layout ownership scanning')
      }
      ownerContext.clearRect(0, 0, snapshot.width, snapshot.height)
      ownerContext.fillStyle = '#fff'
      ownerContext.font = `${snapshot.fontSize}px ${JSON.stringify(snapshot.fontName)}`
      ownerContext.textBaseline = snapshot.baseline
      ownerContext.textAlign = 'right'
      ownerContext.direction = 'rtl'
      // Some canvas factories/mocks hand back the SAME canvas for both calls;
      // the per-word clearRect below would then erase the visible paint, so
      // ownership painting is skipped and every pixel falls back to geometric
      // attribution.
      const canPaintOwners = ownerCanvas !== canvas && ownerContext !== context
      // 1-based word position per pixel; later words overwrite earlier ones,
      // reproducing the main canvas painter's order for overlapping ink.
      const ownerMap = new Uint32Array(snapshot.width * snapshot.height)

      const words: RasterWord[] = []
      const letters: RasterLetter[] = []
      const lines = snapshot.text.split('\n')
      // Nastaliq ink rises ~2x the font size above the baseline and descends
      // below it (~2.5x total), so adjacent lines collide at a Latin-style
      // lineHeight. Enforce a script-safe minimum baseline-to-baseline advance
      // so multi-line verses never overlap (matching the v1 engine, which sizes
      // line spacing from measured ascent/descent).
      const NASTALIQ_MIN_LINE_ADVANCE = 2.75
      const lineAdvance = snapshot.fontSize * Math.max(snapshot.lineHeight, NASTALIQ_MIN_LINE_ADVANCE)
      const wordGap = snapshot.fontSize * snapshot.wordSpacing
      const wordHeight = snapshot.fontSize * 1.25
      const baselineOffset = snapshot.baseline === 'top'
        ? 0
        : snapshot.baseline === 'middle'
          ? wordHeight * 0.5
          : snapshot.baseline === 'bottom'
            ? wordHeight
            : snapshot.fontSize

      // Plan the block first without painting: Nastaliq ink routinely rises
      // 1.5-2x the font size above the baseline and can overhang the advance
      // box horizontally, so painting at the raw cursor positions clips ink at
      // the canvas edges. Measure every word, accumulate the block's ink
      // bounding box, then shift the whole block so the ink fits. Measured ink
      // boxes are geometric; antialiasing can light ~1 extra pixel beyond
      // them, so measured sides get a safety pad while synthetic fallbacks
      // keep their exact bounds.
      const measuredInkPad = 1
      interface PlannedWord {
        readonly token: string
        readonly measurement: MeasuredText
        readonly anchorX: number
        readonly baselineY: number
        readonly lineTop: number
        readonly lineIndex: number
      }
      const planned: PlannedWord[] = []
      let inkMinX = Number.POSITIVE_INFINITY
      let inkMaxX = Number.NEGATIVE_INFINITY
      let inkMinY = Number.POSITIVE_INFINITY
      let inkMaxY = Number.NEGATIVE_INFINITY
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
        const line = lines[lineIndex] ?? ''
        const tokens = line.match(/\S+/gu) ?? []
        const tokenMeasurements = tokens.map((token) => measuredText(context, token))
        const tokenWidths = tokenMeasurements.map((measurement) => measurement.width)
        // Place words by their ADVANCE widths (matching the v1 engine), not by
        // their measured ink edges. In Nastaliq the ink overhang past the
        // advance box (swashes, kashida tails, the ک/گ head of a connector) is
        // large and swings wildly per word — که overhangs ~1x the font size to
        // the right of its pen, در overhangs nothing. Anchoring each word so
        // that varying ink tip lands on the cursor throws away the font's
        // designed pen rhythm, so the *visible* spacing between word bodies
        // becomes uneven (1px between one pair, 100px between the next) even
        // though the extreme ink tips are evenly spaced. Advance-width stepping
        // keeps a constant pen rhythm; the swashes then overhang into the
        // space above/below their neighbours exactly as calligraphy intends,
        // and the ownership union below still attributes every overhanging
        // pixel to its word so no ink is dropped.
        const tokenGaps = tokens.map((token, tokenIndex) => {
          const nextToken = tokens[tokenIndex + 1]
          return nextToken === undefined ? 0 : adjustedWordGap(wordGap, snapshot.fontSize, token, nextToken)
        })
        const contentWidth = tokenWidths.reduce((total, width) => total + width, 0)
          + tokenGaps.reduce((total, gap) => total + gap, 0)
        const lineTop = lineIndex * lineAdvance
        const baselineY = lineTop + baselineOffset
        let cursorRight = snapshot.textAlign === 'right'
          ? snapshot.width
          : snapshot.textAlign === 'center'
            ? snapshot.width * 0.5 + contentWidth * 0.5
            : contentWidth

        for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += 1) {
          const token = tokens[tokenIndex]
          const wordWidth = tokenWidths[tokenIndex]
          const wordMeasurement = tokenMeasurements[tokenIndex]
          if (token === undefined || wordWidth === undefined || wordMeasurement === undefined) continue
          // cursorRight is this word's advance right edge — where the pen sits
          // for textAlign 'right'. Ink may overhang past it on either side; the
          // block-fit pass below shifts the whole block so no ink is clipped.
          const anchorX = cursorRight
          planned.push({ token, measurement: wordMeasurement, anchorX, baselineY, lineTop, lineIndex })
          const horizontal = measuredHorizontalBounds(wordMeasurement, anchorX)
          const vertical = measuredVerticalBounds(wordMeasurement, baselineY, lineTop, wordHeight)
          const horizontalPad = wordMeasurement.actualBoundingBoxLeft === undefined ? 0 : measuredInkPad
          const verticalPad = wordMeasurement.actualBoundingBoxAscent === undefined ? 0 : measuredInkPad
          inkMinX = Math.min(inkMinX, horizontal.x - horizontalPad)
          inkMaxX = Math.max(inkMaxX, horizontal.x + horizontal.width + horizontalPad)
          inkMinY = Math.min(inkMinY, vertical.y - verticalPad)
          inkMaxY = Math.max(inkMaxY, vertical.y + vertical.height + verticalPad)
          // Step left by this word's advance width plus the inter-word gap so
          // the next word keeps the constant pen rhythm.
          cursorRight = anchorX - wordWidth - (tokenGaps[tokenIndex] ?? 0)
        }
      }

      // Place the block inside the canvas. When it fits, center it on both
      // axes (matching the V1 presentation — alignment orders words within
      // the block, not the block within the canvas). When it overflows an
      // axis, clamp instead: the right edge wins horizontally (RTL reading
      // starts there), the top edge wins vertically (sarkesh strokes must
      // never be beheaded).
      let fitOffsetX = 0
      let fitOffsetY = 0
      if (planned.length > 0) {
        const inkWidth = inkMaxX - inkMinX
        const inkHeight = inkMaxY - inkMinY
        if (inkWidth <= snapshot.width) {
          fitOffsetX = (snapshot.width - inkWidth) / 2 - inkMinX
        } else {
          if (inkMinX < 0) fitOffsetX = -inkMinX
          if (inkMaxX + fitOffsetX > snapshot.width) fitOffsetX = snapshot.width - inkMaxX
        }
        if (inkHeight <= snapshot.height) {
          fitOffsetY = (snapshot.height - inkHeight) / 2 - inkMinY
        } else {
          if (inkMaxY > snapshot.height) fitOffsetY = snapshot.height - inkMaxY
          if (inkMinY + fitOffsetY < 0) fitOffsetY = -inkMinY
        }
      }

      for (const item of planned) {
        const anchorX = item.anchorX + fitOffsetX
        const baselineY = item.baselineY + fitOffsetY
        const wordIndex = words.length
        const wordWidth = item.measurement.width
        const wordHorizontalBounds = measuredHorizontalBounds(item.measurement, anchorX)
        const wordVerticalBounds = measuredVerticalBounds(
          item.measurement,
          baselineY,
          item.lineTop + fitOffsetY,
          wordHeight,
        )
        const word: RasterWord = {
          index: wordIndex,
          text: item.token,
          lineIndex: item.lineIndex,
          x: wordHorizontalBounds.x,
          y: wordVerticalBounds.y,
          width: wordHorizontalBounds.width,
          height: wordVerticalBounds.height,
          baselineY,
          anchorX,
        }
        words.push(word)
        context.fillText(item.token, anchorX, baselineY)
        if (canPaintOwners) {
          // Paint ONLY this word into the scratch ownership canvas, then read
          // back its padded bounding rect. Because this scratch contains only
          // this word, every nonzero-alpha pixel has an exact owner, including
          // thin strokes and antialiasing fringes. If ink reaches a crop edge,
          // grow the crop until the edge is clear (or the canvas edge wins) so
          // metric-underreported swashes are not silently clipped.
          ownerContext.clearRect(0, 0, snapshot.width, snapshot.height)
          ownerContext.fillText(item.token, anchorX, baselineY)
          let ownerPad = 4
          const maximumPad = Math.max(snapshot.width, snapshot.height)
          while (ownerPad <= maximumPad) {
            const ownerLeft = Math.max(0, Math.floor(word.x) - ownerPad)
            const ownerTop = Math.max(0, Math.floor(word.y) - ownerPad)
            const ownerRight = Math.min(snapshot.width, Math.ceil(word.x + word.width) + ownerPad)
            const ownerBottom = Math.min(snapshot.height, Math.ceil(word.y + word.height) + ownerPad)
            const ownerWidth = ownerRight - ownerLeft
            const ownerHeight = ownerBottom - ownerTop
            if (ownerWidth <= 0 || ownerHeight <= 0) break
            const rgba = ownerContext.getImageData(ownerLeft, ownerTop, ownerWidth, ownerHeight).data
            let cropEdgeHasInk = false
            for (let localY = 0; localY < ownerHeight; localY += 1) {
              for (let localX = 0; localX < ownerWidth; localX += 1) {
                if ((rgba[(localY * ownerWidth + localX) * 4 + 3] ?? 0) === 0) continue
                if (
                  (localX === 0 && ownerLeft > 0)
                  || (localX === ownerWidth - 1 && ownerRight < snapshot.width)
                  || (localY === 0 && ownerTop > 0)
                  || (localY === ownerHeight - 1 && ownerBottom < snapshot.height)
                ) cropEdgeHasInk = true
              }
            }
            if (cropEdgeHasInk && ownerPad < maximumPad) {
              ownerPad = Math.min(maximumPad, ownerPad * 2)
              continue
            }
            for (let localY = 0; localY < ownerHeight; localY += 1) {
              const rowStart = (ownerTop + localY) * snapshot.width + ownerLeft
              for (let localX = 0; localX < ownerWidth; localX += 1) {
                if ((rgba[(localY * ownerWidth + localX) * 4 + 3] ?? 0) > 0) {
                  ownerMap[rowStart + localX] = wordIndex + 1
                }
              }
            }
            break
          }
        }

        const characters = Array.from(item.token)
        let previousAdvance = 0
        let prefix = ''
        for (let characterIndex = 0; characterIndex < characters.length; characterIndex += 1) {
          const character = characters[characterIndex]
          if (character === undefined) continue
          prefix += character
          const measuredAdvance = characterIndex === characters.length - 1
            ? wordWidth
            : measuredText(context, prefix).width
          const nextAdvance = Math.max(previousAdvance, Math.min(wordWidth, measuredAdvance))
          letters.push({
            index: letters.length,
            wordIndex,
            text: character,
            x: anchorX - nextAdvance,
            y: word.y,
            width: nextAdvance - previousAdvance,
            height: word.height,
            baselineY,
          })
          previousAdvance = nextAdvance
        }
      }

      return {
        width: snapshot.width,
        height: snapshot.height,
        words,
        letters,
        readAlphaStripe(y, rows) {
          const rgba = context.getImageData(0, y, snapshot.width, rows).data
          const alpha = new Uint8Array(snapshot.width * rows)
          for (let index = 0; index < alpha.length; index += 1) {
            alpha[index] = rgba[index * 4 + 3] ?? 0
          }
          return alpha
        },
        readOwnerStripe(y, rows) {
          return ownerMap.slice(y * snapshot.width, (y + rows) * snapshot.width)
        },
      }
    },
  }
}

export interface LayoutScanMetrics {
  dotCandidateChecks: number
  maxDotCandidatesPerLookup: number
  wordCandidateChecks: number
  maxWordCandidatesPerLookup: number
  letterCandidateChecks: number
  maxLetterCandidatesPerLookup: number
}

interface RowIndex {
  readonly offsets: Uint32Array
  readonly indices: Uint32Array
  readonly prefixMaxEnd: Float64Array
}

interface GroupedIntervalIndex {
  readonly offsets: Uint32Array
  readonly indices: Uint32Array
  readonly prefixMaxEnd: Float64Array
}

interface Component {
  area: number
  minX: number
  minY: number
  maxX: number
  maxY: number
}

interface InternalDot extends Component {
  index: number
  wordIndex: number
  letterIndex: number
}

function buildRowIndex(
  width: number,
  height: number,
  intervals: readonly {
    readonly x: number
    readonly y: number
    readonly width: number
    readonly height: number
  }[],
): RowIndex {
  const counts = new Uint32Array(height)
  let referenceCount = 0
  for (let index = 0; index < intervals.length; index += 1) {
    const interval = intervals[index]
    if (interval === undefined) continue
    if (interval.x >= width || interval.x + interval.width <= 0 || interval.height <= 0) continue
    const firstRow = Math.max(0, Math.floor(interval.y))
    const lastRow = Math.min(height, Math.ceil(interval.y + interval.height))
    for (let row = firstRow; row < lastRow; row += 1) {
      referenceCount += 1
      if (referenceCount > MAX_LAYOUT_INTERVAL_REFERENCES) {
        throw new RangeError('Raster ownership index exceeds the interval-reference budget')
      }
      counts[row] = (counts[row] ?? 0) + 1
    }
  }
  const offsets = new Uint32Array(height + 1)
  for (let row = 0; row < height; row += 1) {
    offsets[row + 1] = (offsets[row] ?? 0) + (counts[row] ?? 0)
  }
  const indices = new Uint32Array(offsets[height] ?? 0)
  const cursors = offsets.slice(0, height)
  for (let index = 0; index < intervals.length; index += 1) {
    const interval = intervals[index]
    if (interval === undefined) continue
    if (interval.x >= width || interval.x + interval.width <= 0 || interval.height <= 0) continue
    const firstRow = Math.max(0, Math.floor(interval.y))
    const lastRow = Math.min(height, Math.ceil(interval.y + interval.height))
    for (let row = firstRow; row < lastRow; row += 1) {
      const cursor = cursors[row] ?? 0
      indices[cursor] = index
      cursors[row] = cursor + 1
    }
  }
  const prefixMaxEnd = new Float64Array(indices.length)
  for (let row = 0; row < height; row += 1) {
    const start = offsets[row] ?? 0
    const end = offsets[row + 1] ?? start
    indices.subarray(start, end).sort((left, right) => {
      const leftInterval = intervals[left]
      const rightInterval = intervals[right]
      return (leftInterval?.x ?? 0) - (rightInterval?.x ?? 0) || left - right
    })
    let maxEnd = Number.NEGATIVE_INFINITY
    for (let cursor = start; cursor < end; cursor += 1) {
      const interval = intervals[indices[cursor] ?? 0]
      maxEnd = Math.max(maxEnd, (interval?.x ?? 0) + (interval?.width ?? 0))
      prefixMaxEnd[cursor] = maxEnd
    }
  }
  return { offsets, indices, prefixMaxEnd }
}

function buildGroupedIntervalIndex<T>(
  groupCount: number,
  items: readonly T[],
  groupOf: (item: T) => number,
  startOf: (item: T) => number,
  endOf: (item: T) => number,
): GroupedIntervalIndex {
  const counts = new Uint32Array(groupCount)
  for (const item of items) counts[groupOf(item)] = (counts[groupOf(item)] ?? 0) + 1
  const offsets = new Uint32Array(groupCount + 1)
  for (let group = 0; group < groupCount; group += 1) {
    offsets[group + 1] = (offsets[group] ?? 0) + (counts[group] ?? 0)
  }
  const indices = new Uint32Array(items.length)
  const cursors = offsets.slice(0, groupCount)
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index]
    if (item === undefined) continue
    const group = groupOf(item)
    const cursor = cursors[group] ?? 0
    indices[cursor] = index
    cursors[group] = cursor + 1
  }
  const prefixMaxEnd = new Float64Array(items.length)
  for (let group = 0; group < groupCount; group += 1) {
    const start = offsets[group] ?? 0
    const end = offsets[group + 1] ?? start
    indices.subarray(start, end).sort((left, right) => {
      const leftItem = items[left]
      const rightItem = items[right]
      return (leftItem === undefined ? 0 : startOf(leftItem))
        - (rightItem === undefined ? 0 : startOf(rightItem)) || left - right
    })
    let maxEnd = Number.NEGATIVE_INFINITY
    for (let cursor = start; cursor < end; cursor += 1) {
      const item = items[indices[cursor] ?? 0]
      maxEnd = Math.max(maxEnd, item === undefined ? maxEnd : endOf(item))
      prefixMaxEnd[cursor] = maxEnd
    }
  }
  return { offsets, indices, prefixMaxEnd }
}

function findWord(
  x: number,
  y: number,
  words: readonly RasterWord[],
  rows: RowIndex,
  metrics?: LayoutScanMetrics,
): number {
  const start = rows.offsets[y] ?? 0
  const end = rows.offsets[y + 1] ?? start
  let low = start
  let high = end
  while (low < high) {
    const middle = low + Math.floor((high - low) * 0.5)
    const word = words[rows.indices[middle] ?? 0]
    if ((word?.x ?? Number.POSITIVE_INFINITY) <= x) low = middle + 1
    else high = middle
  }
  let selected = -1
  let selectedOwner = NO_LAYOUT_OWNER
  let baselineDistance = Number.POSITIVE_INFINITY
  let checks = 0
  for (let cursor = low - 1; cursor >= start; cursor -= 1) {
    if ((rows.prefixMaxEnd[cursor] ?? Number.NEGATIVE_INFINITY) <= x) break
    checks += 1
    const wordPosition = rows.indices[cursor]
    if (wordPosition === undefined) continue
    const word = words[wordPosition]
    if (word === undefined || x < word.x || x >= word.x + word.width) continue
    const distance = Math.abs(y - word.baselineY)
    if (distance < baselineDistance || (distance === baselineDistance && word.index < selectedOwner)) {
      selected = wordPosition
      selectedOwner = word.index
      baselineDistance = distance
    }
  }
  if (metrics !== undefined) {
    metrics.wordCandidateChecks += checks
    metrics.maxWordCandidatesPerLookup = Math.max(metrics.maxWordCandidatesPerLookup, checks)
  }
  return selected
}

function findLetter(
  x: number,
  y: number,
  wordOwner: number,
  letters: readonly RasterLetter[],
  groups: GroupedIntervalIndex,
  metrics?: LayoutScanMetrics,
): number {
  const start = groups.offsets[wordOwner] ?? 0
  const end = groups.offsets[wordOwner + 1] ?? start
  let low = start
  let high = end
  while (low < high) {
    const middle = low + Math.floor((high - low) * 0.5)
    const letter = letters[groups.indices[middle] ?? 0]
    if ((letter?.x ?? Number.POSITIVE_INFINITY) <= x) low = middle + 1
    else high = middle
  }
  let selected = -1
  let selectedOwner = NO_LAYOUT_OWNER
  let baselineDistance = Number.POSITIVE_INFINITY
  let checks = 0
  for (let cursor = low - 1; cursor >= start; cursor -= 1) {
    if ((groups.prefixMaxEnd[cursor] ?? Number.NEGATIVE_INFINITY) <= x) break
    checks += 1
    const letterPosition = groups.indices[cursor]
    if (letterPosition === undefined) continue
    const letter = letters[letterPosition]
    if (
      letter === undefined
      || letter.wordIndex !== wordOwner
      || x < letter.x
      || x >= letter.x + letter.width
    ) continue
    const distance = Math.abs(y - letter.baselineY)
    if (distance < baselineDistance || (distance === baselineDistance && letter.index < selectedOwner)) {
      selected = letterPosition
      selectedOwner = letter.index
      baselineDistance = distance
    }
  }
  if (metrics !== undefined) {
    metrics.letterCandidateChecks += checks
    metrics.maxLetterCandidatesPerLookup = Math.max(metrics.maxLetterCandidatesPerLookup, checks)
  }
  return selected
}

/**
 * Distance from a point to an axis-aligned rectangle (zero when inside).
 * Used to attribute lit pixels that fall outside every ownership rectangle —
 * antialiasing fringes and shaped-ink overhang the font metrics underreport —
 * to their closest word/letter instead of silently dropping them.
 */
function rectDistanceSquared(
  x: number,
  y: number,
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number,
): number {
  const dx = x < rectX ? rectX - x : x > rectX + rectWidth ? x - (rectX + rectWidth) : 0
  const dy = y < rectY ? rectY - y : y > rectY + rectHeight ? y - (rectY + rectHeight) : 0
  return dx * dx + dy * dy
}

function nearestWord(x: number, y: number, words: readonly RasterWord[]): number {
  let selected = -1
  let bestDistance = Number.POSITIVE_INFINITY
  let baselineDistance = Number.POSITIVE_INFINITY
  for (let position = 0; position < words.length; position += 1) {
    const word = words[position]
    if (word === undefined) continue
    const distance = rectDistanceSquared(x, y, word.x, word.y, word.width, word.height)
    const toBaseline = Math.abs(y - word.baselineY)
    if (distance < bestDistance || (distance === bestDistance && toBaseline < baselineDistance)) {
      bestDistance = distance
      baselineDistance = toBaseline
      selected = position
    }
  }
  return selected
}

/**
 * Compatibility owner lookup with a 2px dilation. The production rasterizer
 * stamps every nonzero-alpha pixel exactly; dilation remains useful for custom
 * or test rasterizers that publish sparse owner maps.
 */
function ownerAt(owners: Uint32Array, width: number, height: number, x: number, y: number): number {
  const direct = owners[y * width + x] ?? 0
  if (direct !== 0) return direct
  for (let radius = 1; radius <= 2; radius += 1) {
    const rowTop = Math.max(0, y - radius)
    const rowBottom = Math.min(height - 1, y + radius)
    const columnLeft = Math.max(0, x - radius)
    const columnRight = Math.min(width - 1, x + radius)
    for (let row = rowTop; row <= rowBottom; row += 1) {
      for (let column = columnLeft; column <= columnRight; column += 1) {
        if (Math.max(Math.abs(column - x), Math.abs(row - y)) !== radius) continue
        const owner = owners[row * width + column] ?? 0
        if (owner !== 0) return owner
      }
    }
  }
  return 0
}

function nearestLetter(
  x: number,
  y: number,
  wordOwner: number,
  letters: readonly RasterLetter[],
  groups: GroupedIntervalIndex,
): number {
  const start = groups.offsets[wordOwner] ?? 0
  const end = groups.offsets[wordOwner + 1] ?? start
  let selected = -1
  let bestDistance = Number.POSITIVE_INFINITY
  for (let cursor = start; cursor < end; cursor += 1) {
    const letterPosition = groups.indices[cursor]
    if (letterPosition === undefined) continue
    const letter = letters[letterPosition]
    if (letter === undefined || letter.wordIndex !== wordOwner) continue
    const distance = rectDistanceSquared(x, y, letter.x, letter.y, letter.width, letter.height)
    if (
      distance < bestDistance
      || (distance === bestDistance && selected >= 0 && letterPosition < selected)
    ) {
      bestDistance = distance
      selected = letterPosition
    }
  }
  return selected
}

function cropAreaForLetter(letter: RasterLetter, width: number, height: number): number {
  const rightEdge = letter.x + letter.width
  const bottomEdge = letter.y + letter.height
  if (!Number.isFinite(rightEdge) || !Number.isFinite(bottomEdge)) {
    throw new RangeError('Raster letter crop bounds must be finite')
  }
  const left = Math.max(0, Math.min(width, Math.floor(letter.x)))
  const top = Math.max(0, Math.min(height, Math.floor(letter.y)))
  const right = Math.max(0, Math.min(width, Math.ceil(rightEdge)))
  const bottom = Math.max(0, Math.min(height, Math.ceil(bottomEdge)))
  return Math.max(0, right - left) * Math.max(0, bottom - top)
}

function assertRasterMetadataArrays(raster: LayoutRaster): void {
  if (!Array.isArray(raster.words) || !Array.isArray(raster.letters)) {
    throw new TypeError('Raster metadata must use arrays')
  }
}

function validateRasterMetadata(raster: LayoutRaster, snapshot: PackedLayoutSnapshot): number {
  assertRasterMetadataArrays(raster)
  const emptyFloat = new Float32Array(0)
  const emptyOwner = new Uint32Array(0)
  validateLayoutResponse({
    type: 'layout-result', revision: 0,
    result: {
      targets: {
        count: 0, x: emptyFloat, y: emptyFloat, size: emptyFloat,
        wordIndex: emptyOwner, letterIndex: emptyOwner, dotIndex: emptyOwner,
      },
      dots: {
        count: 0, x: emptyFloat, y: emptyFloat, width: emptyFloat, height: emptyFloat,
        relativeX: emptyFloat, relativeY: emptyFloat,
        wordIndex: emptyOwner, letterIndex: emptyOwner,
      },
      words: raster.words,
      letters: raster.letters,
    },
  }, snapshot.particleBudget)
  if (raster.words.length > MAX_LAYOUT_OFFSET_COUNT || raster.letters.length > MAX_LAYOUT_OFFSET_COUNT) {
    throw new RangeError('Raster metadata exceeds the ownership budget')
  }
  for (const word of raster.words) {
    if (word.text.length > MAX_LAYOUT_TEXT_LENGTH) throw new RangeError('Raster word text exceeds its budget')
  }
  let maxCropArea = 0
  for (const letter of raster.letters) {
    const cropArea = cropAreaForLetter(letter, snapshot.width, snapshot.height)
    if (cropArea > MAX_LAYOUT_COMPONENT_SCRATCH_PIXELS) {
      throw new RangeError(`Raster letter crop exceeds the ${MAX_LAYOUT_COMPONENT_SCRATCH_PIXELS}-pixel scratch budget`)
    }
    maxCropArea = Math.max(maxCropArea, cropArea)
  }
  return maxCropArea
}

function detectDots(
  alpha: Uint8Array,
  width: number,
  height: number,
  letters: readonly RasterLetter[],
  maxCropArea: number,
): InternalDot[] {
  const visited = new Uint8Array(maxCropArea)
  const stack = new Int32Array(maxCropArea)
  const componentCapacity = Math.min(maxCropArea, MAX_LAYOUT_DOT_COUNT + 1)
  const componentArea = new Uint32Array(componentCapacity)
  const componentMinX = new Int32Array(componentCapacity)
  const componentMinY = new Int32Array(componentCapacity)
  const componentMaxX = new Int32Array(componentCapacity)
  const componentMaxY = new Int32Array(componentCapacity)
  const dots: InternalDot[] = []

  for (const letter of letters) {
    const left = Math.max(0, Math.floor(letter.x))
    const top = Math.max(0, Math.floor(letter.y))
    const right = Math.min(width, Math.ceil(letter.x + letter.width))
    const bottom = Math.min(height, Math.ceil(letter.y + letter.height))
    const cropWidth = right - left
    const cropHeight = bottom - top
    const cropArea = cropWidth * cropHeight
    if (cropArea <= 0) continue
    visited.fill(0, 0, cropArea)
    let componentCount = 0

    for (let localY = 0; localY < cropHeight; localY += 1) {
      for (let localX = 0; localX < cropWidth; localX += 1) {
        const localIndex = localY * cropWidth + localX
        if (visited[localIndex] !== 0 || alpha[(top + localY) * width + left + localX] === 0) continue
        if (componentCount > MAX_LAYOUT_DOT_COUNT - dots.length) {
          throw new RangeError(`Layout dot count exceeds the ${MAX_LAYOUT_DOT_COUNT}-dot budget`)
        }
        let stackSize = 1
        stack[0] = localIndex
        visited[localIndex] = 1
        let area = 0
        let minX = left + localX
        let minY = top + localY
        let maxX = minX
        let maxY = minY
        while (stackSize > 0) {
          stackSize -= 1
          const pixel = stack[stackSize] ?? 0
          const pixelY = Math.floor(pixel / cropWidth)
          const pixelX = pixel - pixelY * cropWidth
          const canvasX = left + pixelX
          const canvasY = top + pixelY
          area += 1
          minX = Math.min(minX, canvasX)
          minY = Math.min(minY, canvasY)
          maxX = Math.max(maxX, canvasX)
          maxY = Math.max(maxY, canvasY)
          for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
            for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
              if (offsetX === 0 && offsetY === 0) continue
              const nextX = pixelX + offsetX
              const nextY = pixelY + offsetY
              if (nextX < 0 || nextX >= cropWidth || nextY < 0 || nextY >= cropHeight) continue
              const next = nextY * cropWidth + nextX
              if (visited[next] !== 0 || alpha[(top + nextY) * width + left + nextX] === 0) continue
              visited[next] = 1
              stack[stackSize] = next
              stackSize += 1
            }
          }
        }
        componentArea[componentCount] = area
        componentMinX[componentCount] = minX
        componentMinY[componentCount] = minY
        componentMaxX[componentCount] = maxX
        componentMaxY[componentCount] = maxY
        componentCount += 1
      }
    }
    if (componentCount <= 1) continue
    let mainComponent = 0
    for (let index = 1; index < componentCount; index += 1) {
      if ((componentArea[index] ?? 0) > (componentArea[mainComponent] ?? 0)) mainComponent = index
    }
    for (let index = 0; index < componentCount; index += 1) {
      if (index === mainComponent) continue
      if (dots.length >= MAX_LAYOUT_DOT_COUNT) {
        throw new RangeError(`Layout dot count exceeds the ${MAX_LAYOUT_DOT_COUNT}-dot budget`)
      }
      dots.push({
        area: componentArea[index] ?? 0,
        minX: componentMinX[index] ?? 0,
        minY: componentMinY[index] ?? 0,
        maxX: componentMaxX[index] ?? 0,
        maxY: componentMaxY[index] ?? 0,
        index: dots.length,
        wordIndex: letter.wordIndex,
        letterIndex: letter.index,
      })
    }
  }
  return dots
}

function findDot(
  x: number,
  y: number,
  letterOwner: number,
  dots: readonly InternalDot[],
  groups: GroupedIntervalIndex,
  metrics?: LayoutScanMetrics,
): number {
  if (letterOwner === NO_LAYOUT_OWNER) return -1
  const start = groups.offsets[letterOwner] ?? 0
  const end = groups.offsets[letterOwner + 1] ?? start
  let low = start
  let high = end
  while (low < high) {
    const middle = low + Math.floor((high - low) * 0.5)
    const dot = dots[groups.indices[middle] ?? 0]
    if ((dot?.minX ?? Number.POSITIVE_INFINITY) <= x) low = middle + 1
    else high = middle
  }
  let checks = 0
  for (let cursor = low - 1; cursor >= start; cursor -= 1) {
    if ((groups.prefixMaxEnd[cursor] ?? Number.NEGATIVE_INFINITY) < x) break
    checks += 1
    const dotPosition = groups.indices[cursor]
    if (dotPosition === undefined) continue
    const dot = dots[dotPosition]
    if (
      dot !== undefined
      && dot.letterIndex === letterOwner
      && x >= dot.minX && x <= dot.maxX
      && y >= dot.minY && y <= dot.maxY
    ) {
      if (metrics !== undefined) {
        metrics.dotCandidateChecks += checks
        metrics.maxDotCandidatesPerLookup = Math.max(metrics.maxDotCandidatesPerLookup, checks)
      }
      return dotPosition
    }
  }
  if (metrics !== undefined) {
    metrics.dotCandidateChecks += checks
    metrics.maxDotCandidatesPerLookup = Math.max(metrics.maxDotCandidatesPerLookup, checks)
  }
  return -1
}

function hashUnit(seed: number, x: number, y: number, salt: number): number {
  let value = (seed >>> 0) ^ Math.imul(x + 1, 0x9e37_79b1) ^ Math.imul(y + 1, 0x85eb_ca6b) ^ salt
  value = Math.imul(value ^ (value >>> 16), 0xc2b2_ae35)
  value = (value ^ (value >>> 13)) >>> 0
  return value / 4_294_967_296
}

function offsetAt(offsets: Float32Array, owner: number, axis: 0 | 1): number {
  if (owner === NO_LAYOUT_OWNER || owner * 2 + axis >= offsets.length) return 0
  return offsets[owner * 2 + axis] ?? 0
}

function emptyTargets(count: number): LayoutTargetBuffers {
  return {
    count,
    x: new Float32Array(count),
    y: new Float32Array(count),
    size: new Float32Array(count),
    wordIndex: new Uint32Array(count),
    letterIndex: new Uint32Array(count),
    dotIndex: new Uint32Array(count),
  }
}

function createDotBuffers(
  dots: readonly InternalDot[],
  letters: readonly RasterLetter[],
  snapshot: PackedLayoutSnapshot,
): LayoutDotBuffers {
  const count = dots.length
  const buffers: LayoutDotBuffers = {
    count,
    x: new Float32Array(count), y: new Float32Array(count),
    width: new Float32Array(count), height: new Float32Array(count),
    relativeX: new Float32Array(count), relativeY: new Float32Array(count),
    wordIndex: new Uint32Array(count), letterIndex: new Uint32Array(count),
  }
  for (let index = 0; index < count; index += 1) {
    const dot = dots[index]
    if (dot === undefined) continue
    const candidate = letters[dot.letterIndex]
    const letter = candidate?.index === dot.letterIndex ? candidate : undefined
    const centerX = (dot.minX + dot.maxX) * 0.5
    const centerY = (dot.minY + dot.maxY) * 0.5
    const wordX = offsetAt(snapshot.wordOffsets, dot.wordIndex, 0)
    const wordY = offsetAt(snapshot.wordOffsets, dot.wordIndex, 1)
    const letterX = offsetAt(snapshot.letterOffsets, dot.letterIndex, 0)
    const letterY = offsetAt(snapshot.letterOffsets, dot.letterIndex, 1)
    const dotX = offsetAt(snapshot.dotOffsets, dot.index, 0)
    const dotY = offsetAt(snapshot.dotOffsets, dot.index, 1)
    buffers.x[index] = centerX + wordX + letterX + dotX
    buffers.y[index] = centerY + wordY + letterY + dotY
    buffers.width[index] = dot.maxX - dot.minX + 1
    buffers.height[index] = dot.maxY - dot.minY + 1
    buffers.relativeX[index] = letter === undefined ? 0 : centerX - (letter.x + letter.width * 0.5)
    buffers.relativeY[index] = letter === undefined ? 0 : centerY - (letter.y + letter.height * 0.5)
    buffers.wordIndex[index] = dot.wordIndex
    buffers.letterIndex[index] = dot.letterIndex
  }
  return buffers
}

export async function scanLayout(
  snapshot: PackedLayoutSnapshot,
  rasterizer: LayoutRasterizer,
  metrics?: LayoutScanMetrics,
): Promise<LayoutResult> {
  if (metrics !== undefined) {
    metrics.dotCandidateChecks = 0
    metrics.maxDotCandidatesPerLookup = 0
    metrics.wordCandidateChecks = 0
    metrics.maxWordCandidatesPerLookup = 0
    metrics.letterCandidateChecks = 0
    metrics.maxLetterCandidatesPerLookup = 0
  }
  validateLayoutRequest({ type: 'layout', revision: 0, snapshot })
  const raster = await rasterizer.rasterize(snapshot)
  if (raster.width !== snapshot.width || raster.height !== snapshot.height) {
    throw new RangeError('Raster dimensions must match the layout snapshot')
  }
  const maxCropArea = validateRasterMetadata(raster, snapshot)
  const alpha = new Uint8Array(snapshot.width * snapshot.height)
  for (let y = 0; y < snapshot.height; y += MAX_LAYOUT_STRIPE_ROWS) {
    const rows = Math.min(MAX_LAYOUT_STRIPE_ROWS, snapshot.height - y)
    const stripe = raster.readAlphaStripe(y, rows)
    if (!(stripe instanceof Uint8Array) || stripe.length !== snapshot.width * rows) {
      throw new RangeError('Raster alpha stripe has an invalid buffer length')
    }
    alpha.set(stripe, y * snapshot.width)
  }
  const owners = new Uint32Array(snapshot.width * snapshot.height)
  for (let y = 0; y < snapshot.height; y += MAX_LAYOUT_STRIPE_ROWS) {
    const rows = Math.min(MAX_LAYOUT_STRIPE_ROWS, snapshot.height - y)
    const stripe = raster.readOwnerStripe(y, rows)
    if (!(stripe instanceof Uint32Array) || stripe.length !== snapshot.width * rows) {
      throw new RangeError('Raster owner stripe has an invalid buffer length')
    }
    owners.set(stripe, y * snapshot.width)
  }

  const dots = detectDots(alpha, snapshot.width, snapshot.height, raster.letters, maxCropArea)
  // Word ownership is ink-truth first: the owner map names the word whose
  // paint actually covers each pixel, so overhanging strokes/dots that spill
  // into a neighbour's box still follow their own word's offsets. Pixels the
  // owner map leaves at 0 (rasterizers without full ink readback) first ask
  // ownerAt, which dilates sparse ownership over a 2px Chebyshev
  // neighbourhood; anything still unowned falls back to word rectangles
  // padded by 1px — the previous behaviour — so the fringe resolves through
  // the indexed path (with its closest-baseline tie-break), and the linear
  // nearest-word lookup stays as the final safety net for larger metric
  // underreporting.
  const ownershipPad = 1
  const paddedWords: RasterWord[] = raster.words.map((word) => ({
    ...word,
    x: word.x - ownershipPad,
    y: word.y - ownershipPad,
    width: word.width + ownershipPad * 2,
    height: word.height + ownershipPad * 2,
  }))
  const wordRows = buildRowIndex(snapshot.width, snapshot.height, paddedWords)
  const letterGroups = buildGroupedIntervalIndex(
    raster.words.length,
    raster.letters,
    (letter) => letter.wordIndex,
    (letter) => letter.x,
    (letter) => letter.x + letter.width,
  )
  const dotGroups = buildGroupedIntervalIndex(
    raster.letters.length,
    dots,
    (dot) => dot.letterIndex,
    (dot) => dot.minX,
    (dot) => dot.maxX,
  )
  // Every lit pixel is a candidate whenever any word exists: the raster is
  // cleared before words are painted, so all ink belongs to some word. Pixels
  // outside every ownership rectangle are attributed to the nearest word
  // below rather than dropped, which would visibly cut the calligraphy.
  let candidateCount = 0
  if (raster.words.length > 0) {
    for (let y = 0; y < snapshot.height; y += snapshot.density) {
      for (let x = 0; x < snapshot.width; x += snapshot.density) {
        if (alpha[y * snapshot.width + x] === 0) continue
        candidateCount += 1
      }
    }
  }
  const outputCount = Math.min(candidateCount, snapshot.particleBudget)
  const targets = emptyTargets(outputCount)
  let candidateOrdinal = 0
  let outputIndex = 0
  let desiredOrdinal = outputCount === 1 ? Math.floor((candidateCount - 1) * 0.5) : 0

  for (let y = 0; y < snapshot.height && outputIndex < outputCount; y += snapshot.density) {
    for (let x = 0; x < snapshot.width && outputIndex < outputCount; x += snapshot.density) {
      if (alpha[y * snapshot.width + x] === 0) continue
      if (candidateOrdinal === desiredOrdinal) {
        const inkOwner = ownerAt(owners, snapshot.width, snapshot.height, x, y)
        let wordPosition = inkOwner >= 1 && inkOwner <= raster.words.length ? inkOwner - 1 : -1
        if (wordPosition < 0) wordPosition = findWord(x, y, paddedWords, wordRows, metrics)
        if (wordPosition < 0) wordPosition = nearestWord(x, y, raster.words)
        if (wordPosition < 0) {
          throw new RangeError('Layout scan invariant violated: a lit pixel found no owning word')
        }
        const word = raster.words[wordPosition]
        if (word === undefined) {
          throw new RangeError('Layout scan invariant violated: word ownership is out of range')
        }
        let letterPosition = findLetter(x, y, word.index, raster.letters, letterGroups, metrics)
        if (letterPosition < 0) letterPosition = nearestLetter(x, y, word.index, raster.letters, letterGroups)
        const letterOwner = letterPosition < 0
          ? NO_LAYOUT_OWNER
          : (raster.letters[letterPosition]?.index ?? NO_LAYOUT_OWNER)
        const dotPosition = findDot(x, y, letterOwner, dots, dotGroups, metrics)
        const dotOwner = dotPosition < 0 ? NO_LAYOUT_OWNER : (dots[dotPosition]?.index ?? NO_LAYOUT_OWNER)
        // Match the v1 engine: a tiny fixed ±1px jitter keeps particles ON the
        // calligraphy strokes. Scaling jitter by density (the old v2 behavior)
        // scattered particles up to ±8.75px off the ink, so correct text read as
        // a jumbled cloud. gridMode still pins particles exactly to the grid.
        const jitterScale = snapshot.gridMode ? 0 : 2
        const jitterX = (hashUnit(snapshot.seed, x, y, 0x13a5_b7d1) - 0.5) * jitterScale
        const jitterY = (hashUnit(snapshot.seed, x, y, 0x91e1_0da5) - 0.5) * jitterScale
        targets.x[outputIndex] = x + jitterX
          + offsetAt(snapshot.wordOffsets, word.index, 0)
          + offsetAt(snapshot.letterOffsets, letterOwner, 0)
          + offsetAt(snapshot.dotOffsets, dotOwner, 0)
        targets.y[outputIndex] = y + jitterY
          + offsetAt(snapshot.wordOffsets, word.index, 1)
          + offsetAt(snapshot.letterOffsets, letterOwner, 1)
          + offsetAt(snapshot.dotOffsets, dotOwner, 1)
        // Match the v1 engine: particle size ~= density * 0.8 with a small
        // ±0.5px variation, instead of density * (0.7..1.3) which produced
        // oversized, overlapping blobs at higher densities.
        targets.size[outputIndex] = snapshot.density * 0.8
          + (hashUnit(snapshot.seed, x, y, 0x7f4a_7c15) - 0.5)
        targets.wordIndex[outputIndex] = word.index
        targets.letterIndex[outputIndex] = letterOwner
        targets.dotIndex[outputIndex] = dotOwner
        outputIndex += 1
        if (outputIndex < outputCount) {
          desiredOrdinal = outputCount === 1
            ? desiredOrdinal
            : Math.round(outputIndex * (candidateCount - 1) / (outputCount - 1))
        }
      }
      candidateOrdinal += 1
    }
  }

  const result: LayoutResult = {
    targets,
    dots: createDotBuffers(dots, raster.letters, snapshot),
    words: raster.words.map((word) => ({ ...word })),
    letters: raster.letters.map((letter) => ({ ...letter })),
  }
  validateLayoutResponse({ type: 'layout-result', revision: 0, result }, snapshot.particleBudget)
  return result
}
